/**
 * 활동 로그 API 서비스
 * ilouli-auth 서버의 활동 로그 API와 통신
 */

import { getToken } from '../shared/auth';

// Auth 서버 URL (활동 로그 API는 auth 서버에서 제공)
const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL || 'https://auth.ilouli.com';

/**
 * 인증 헤더 생성
 */
const getAuthHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

/**
 * API 요청 헬퍼
 */
const authApiRequest = async (endpoint, options = {}) => {
  const response = await fetch(`${AUTH_API_URL}${endpoint}`, {
    headers: getAuthHeaders(),
    ...options
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Request failed');
  }

  return data;
};

/**
 * 사용자별 활동 로그 조회
 * @param {number} userId - 사용자 ID
 * @param {Object} options - 필터 옵션
 */
export const getUserActivityLogs = async (userId, options = {}) => {
  const params = new URLSearchParams();

  if (options.category) params.append('category', options.category);
  if (options.actionType) params.append('action_type', options.actionType);
  if (options.status) params.append('status', options.status);
  if (options.from) params.append('from', options.from);
  if (options.to) params.append('to', options.to);
  if (options.page) params.append('page', options.page);
  if (options.limit) params.append('limit', options.limit);

  const query = params.toString();
  const endpoint = `/api/admin/users/${userId}/activity-logs${query ? `?${query}` : ''}`;

  return authApiRequest(endpoint);
};

/**
 * 전체 활동 로그 조회
 * @param {Object} options - 필터 옵션
 */
export const getAllActivityLogs = async (options = {}) => {
  const params = new URLSearchParams();

  if (options.userId) params.append('user_id', options.userId);
  if (options.category) params.append('category', options.category);
  if (options.actionType) params.append('action_type', options.actionType);
  if (options.status) params.append('status', options.status);
  if (options.service) params.append('service', options.service);
  if (options.from) params.append('from', options.from);
  if (options.to) params.append('to', options.to);
  if (options.search) params.append('search', options.search);
  if (options.page) params.append('page', options.page);
  if (options.limit) params.append('limit', options.limit);

  const query = params.toString();
  const endpoint = `/api/admin/activity-logs${query ? `?${query}` : ''}`;

  return authApiRequest(endpoint);
};

/**
 * 활동 통계 조회
 * @param {Object} options - 필터 옵션
 */
export const getActivityStats = async (options = {}) => {
  const params = new URLSearchParams();

  if (options.userId) params.append('user_id', options.userId);
  if (options.from) params.append('from', options.from);
  if (options.to) params.append('to', options.to);
  if (options.groupBy) params.append('group_by', options.groupBy);

  const query = params.toString();
  const endpoint = `/api/admin/activity-logs/stats${query ? `?${query}` : ''}`;

  return authApiRequest(endpoint);
};

/**
 * 오늘 활동 요약 조회
 */
export const getTodayStats = async () => {
  return authApiRequest('/api/admin/activity-logs/today');
};

/**
 * 최근 활동 로그 조회
 * @param {number} limit - 조회 개수
 */
export const getRecentActivityLogs = async (limit = 50) => {
  return authApiRequest(`/api/admin/activity-logs/recent?limit=${limit}`);
};

/**
 * 활동 타입 한글 변환
 */
export const getActionTypeLabel = (actionType) => {
  const labels = {
    // 인증
    login: '로그인',
    logout: '로그아웃',
    signup: '회원가입',
    token_invalid: '토큰 만료',
    password_change: '비밀번호 변경',
    account_delete: '계정 삭제',

    // 콘텐츠
    post_create: '게시글 작성',
    post_update: '게시글 수정',
    post_delete: '게시글 삭제',
    comment_create: '댓글 작성',
    comment_delete: '댓글 삭제',

    // 상호작용
    like: '좋아요',
    unlike: '좋아요 취소',
    report: '신고',
    report_resolve: '신고 처리',

    // 설정
    nickname_change: '닉네임 변경',
    profile_update: '프로필 수정',
    settings_update: '설정 변경',

    // 활동
    checkin: '출석 체크',
    achievement: '업적 달성',
    level_up: '레벨업',

    // AI
    ai_job_create: 'AI 작업 생성',
    ai_job_complete: 'AI 작업 완료',
    ai_job_fail: 'AI 작업 실패',

    // 관리자
    user_approve: '사용자 승인',
    user_reject: '사용자 거절',
    tier_change: '등급 변경',
    user_suspend: '사용자 정지',
    user_delete: '사용자 삭제'
  };

  return labels[actionType] || actionType;
};

/**
 * 카테고리 한글 변환
 */
export const getCategoryLabel = (category) => {
  const labels = {
    auth: '인증',
    content: '콘텐츠',
    interaction: '상호작용',
    settings: '설정',
    activity: '활동',
    ai: 'AI',
    admin: '관리자'
  };

  return labels[category] || category;
};

/**
 * 서비스명 한글 변환
 */
export const getServiceLabel = (service) => {
  const labels = {
    auth: '인증',
    main: '메인',
    community: '커뮤니티',
    ai: 'AI',
    admin: '관리자',
    family: '가족',
    lab: '실험실',
    stock: '주식',
    work: '업무',
    devteam: '개발팀'
  };

  return labels[service] || service;
};

/**
 * 상태 한글 변환 및 색상
 */
export const getStatusInfo = (status) => {
  const info = {
    success: { label: '성공', color: '#22c55e', bgColor: '#dcfce7' },
    failed: { label: '실패', color: '#ef4444', bgColor: '#fee2e2' },
    pending: { label: '대기', color: '#f59e0b', bgColor: '#fef3c7' }
  };

  return info[status] || { label: status, color: '#6b7280', bgColor: '#f3f4f6' };
};

export default {
  getUserActivityLogs,
  getAllActivityLogs,
  getActivityStats,
  getTodayStats,
  getRecentActivityLogs,
  getActionTypeLabel,
  getCategoryLabel,
  getServiceLabel,
  getStatusInfo
};
